import React from 'react';

/**
 * Reusable animated skeleton / shimmer placeholder for loading states.
 *
 * Usage examples:
 *
 *   <Shimmer.Page />                        // Full-page: title + 4 cards
 *   <Shimmer.List rows={5} />               // Generic list, 5 rows
 *   <Shimmer.Card />                        // A single card block
 *   <Shimmer.Line width="60%" />            // Single line of text
 *   <Shimmer.Title />                       // A big heading placeholder
 *
 * All variants use Tailwind's built-in ``animate-pulse`` on a slate-200
 * background — no CSS keyframes needed. Rounded to match card / pill
 * shapes elsewhere in the app.
 */

const base = 'bg-slate-200/70 animate-pulse rounded-md';

function Line({ width = '100%', className = '' }) {
  return <div className={`${base} h-3 ${className}`} style={{ width }} data-testid="shimmer-line" />;
}

function Title({ width = '40%' }) {
  return <div className={`${base} h-6 mb-3`} style={{ width }} data-testid="shimmer-title" />;
}

function Card({ className = '' }) {
  return (
    <div className={`rounded-2xl border border-slate-100 bg-white p-4 sm:p-5 ${className}`} data-testid="shimmer-card">
      <div className="flex items-center gap-3">
        <div className={`${base} w-10 h-10 rounded-full`} />
        <div className="flex-1 space-y-2">
          <Line width="60%" />
          <Line width="35%" />
        </div>
      </div>
      <div className="mt-4 space-y-2">
        <Line width="90%" />
        <Line width="75%" />
      </div>
    </div>
  );
}

function List({ rows = 4, className = '' }) {
  return (
    <div className={`space-y-3 ${className}`} data-testid="shimmer-list">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white border border-slate-100">
          <div className={`${base} w-9 h-9 rounded-full flex-shrink-0`} />
          <div className="flex-1 space-y-2">
            <Line width={`${60 + ((i * 7) % 30)}%`} />
            <Line width={`${30 + ((i * 11) % 30)}%`} />
          </div>
          <div className={`${base} w-10 h-6 rounded-full flex-shrink-0`} />
        </div>
      ))}
    </div>
  );
}

function Page({ rows = 4, className = '' }) {
  return (
    <div className={`space-y-5 ${className}`} data-testid="shimmer-page">
      <div>
        <Title width="35%" />
        <Line width="70%" />
      </div>
      <List rows={rows} />
    </div>
  );
}

function Inline({ label = 'Loading…' }) {
  return (
    <div className="inline-flex items-center gap-2 text-xs text-slate-500" data-testid="shimmer-inline">
      <span className={`${base} w-3 h-3 rounded-full`} />
      <span className={`${base} h-3 w-20 rounded`} />
      <span className="sr-only">{label}</span>
    </div>
  );
}

const Shimmer = { Line, Title, Card, List, Page, Inline };
export default Shimmer;
